'use client'

import { memo, useCallback, useState } from 'react'
import { IconCalendar, IconMapPin } from '@tabler/icons-react'

import {
  formatDateWithTime,
  formatDateWithWeekday,
  formatDeliveryExtendedData,
  formatPhoneNumber,
  TMSReservationSlotData
} from 'modules/delivery'
import Button from 'shared/ui/Button'
import { ButtonVariants } from 'shared/ui/Button/Button.enums'
import { Modal, ModalDescription, ModalTitle } from 'shared/ui/Modal'

import styles from './CartReservationLabel.module.scss'

type CartReservationLabelProps = {
  reservationSlotData: TMSReservationSlotData
  handleCancelReservation: () => Promise<void>
  cancelLoading: boolean
  handleEditReservation: () => void
}

const CartReservationLabel = ({
  reservationSlotData,
  handleCancelReservation,
  cancelLoading,
  handleEditReservation
}: CartReservationLabelProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  return (
    <>
      <div className={styles.labelWrapper}>
        <div className={styles.addressesWrapper}>
          <div className={styles.addressWrapper}>
            <h2 className={styles.addressTitle}>
              <IconCalendar />
              Termin dostawy
            </h2>
            <p className={styles.addressContent}>
              {formatDateWithWeekday(new Date(reservationSlotData?.slot?.date))}
            </p>
            <p className={styles.addressContent}>
              {formatDeliveryExtendedData(
                reservationSlotData?.slotExtended?.from,
                reservationSlotData?.slotExtended?.to
              )}
            </p>
          </div>
          <div className={styles.addressWrapper}>
            <h2 className={styles.addressTitle}>
              <IconMapPin />
              Adres dostawy
            </h2>
            <p className={styles.addressContent}>
              {reservationSlotData?.location?.additional?.contactName}
            </p>
            <p className={styles.addressContent}>
              {reservationSlotData?.location?.rawAddress}
            </p>
            <p className={styles.addressContent}>
              {formatPhoneNumber(
                reservationSlotData?.location?.additional?.contactPhoneNo
              )}
            </p>
          </div>
        </div>
        <div className={styles.informationWrapper}>
          Złóż zamówienie do{' '}
          <b>{formatDateWithTime(new Date(reservationSlotData.expiration))}</b>,
          po tym terminie rezerwacja zostanie anulowana.
        </div>
        <div className={styles.buttonsWrapper}>
          <Button
            variant='neutralText'
            onClick={handleOpenModal}
            disabled={cancelLoading}
            loading={cancelLoading}
          >
            Odwołaj
          </Button>
          <Button variant='primary' onClick={handleEditReservation}>
            Edytuj
          </Button>
        </div>
      </div>
      <Modal
        id='cart-reservation-cancel-modal'
        open={isModalOpen}
        onClose={handleCloseModal}
        title='Anulowanie rezerwacji'
        size='small'
      >
        <ModalTitle>Anulowanie rezerwacji</ModalTitle>
        <ModalDescription>
          Czy na pewno chcesz anulować rezerwację?
        </ModalDescription>
        <div className={styles.modalButtonsWrapper}>
          <Button
            variant='primary'
            onClick={handleCancelReservation}
            disabled={cancelLoading}
            loading={cancelLoading}
            fullWidth
          >
            Odwołaj
          </Button>
          <Button
            variant={ButtonVariants.neutralOutline}
            onClick={handleCloseModal}
            fullWidth
          >
            Anuluj
          </Button>
        </div>
      </Modal>
    </>
  )
}

export default memo(CartReservationLabel)
